/**
 * src/utils/governanceRbacFlag.js
 *
 * The ONE client-side definition of `state.features.governanceRbacV1`.
 * Twin of azure-functions/shared/governanceRbacFlag.js, kept in step by
 * governanceRbacFlag.parity.test.js — change one, change the other. (src/
 * can import a pure `.cjs` from azure-functions/shared — the governance role
 * rule does, governanceRule.cjs — so a new shared rule needs no twin.)
 *
 * Only an explicit `false` switches governance enforcement off. Absent means
 * ON — the server has enforced it that way all along, and every screen that
 * said otherwise was telling the tenant something untrue (FDNA-01). The
 * string "false" also reads as OFF, as it always has on the server; see the
 * server twin for why, and for how it is surfaced.
 */

export const REQUIRED_GOVERNANCE_ROLES = Object.freeze(['RISK_OWNER', 'REVIEWER', 'APPROVER']);

export const OPT_OUT_REASON_MIN = 10;

export function isOptOutValue(v) {
  return v === false || v === 'false';
}

export function isGovernanceRbacEnabled(features) {
  return !(features && isOptOutValue(features.governanceRbacV1));
}

function isIsoDate(v) {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) && !Number.isNaN(Date.parse(v));
}

export function validateOptOutRequest(optOut) {
  if (!optOut || typeof optOut !== 'object') return 'An opt-out needs a reason and a review date.';
  const reason = typeof optOut.reason === 'string' ? optOut.reason.trim() : '';
  if (reason.length < OPT_OUT_REASON_MIN) {
    return `An opt-out needs a reason of at least ${OPT_OUT_REASON_MIN} characters.`;
  }
  if (!isIsoDate(optOut.reviewBy)) return 'An opt-out needs a review date.';
  return null;
}

export function isCompleteOptOutRecord(optOut) {
  return (
    validateOptOutRequest(optOut) === null &&
    isIsoDate(optOut.at) &&
    typeof optOut.by === 'string' &&
    !!optOut.by
  );
}

/** 'on' | 'opted-out' | 'review-due' | 'legacy' — see the server twin. */
export function governanceRbacStanding(features, nowMs = Date.now()) {
  if (isGovernanceRbacEnabled(features)) return 'on';
  const rec = features.governanceRbacV1OptOut;
  if (!isCompleteOptOutRecord(rec)) return 'legacy';
  return Date.parse(rec.reviewBy) <= nowMs ? 'review-due' : 'opted-out';
}

export function unheldRequiredRoles(assignments, users) {
  const live = Array.isArray(users)
    ? new Set(users.map((u) => u && (u.id || u.userId)).filter(Boolean))
    : null;
  const held = new Set();
  for (const a of assignments || []) {
    if (!a || !a.roleKey) continue;
    if (live && !live.has(a.userId)) continue;
    held.add(a.roleKey);
  }
  return REQUIRED_GOVERNANCE_ROLES.filter((r) => !held.has(r));
}
